import { Autocomplete, Checkbox, TextField } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import FirmNavnId from '../../../models/FirmNavnId';

// Pseudo-option for "Alle datterselskaper" (all subsidiaries) - never stored in the selection itself.
const ALL_SUBSIDIARIES = new FirmNavnId(-1, 'Alle datterselskaper');

interface Props {
  /** The logged-in company (hovedfirma, the main company). */
  mainCompany: FirmNavnId;
  /** The group's companies from /admin/getalladminFirmfromdb; the main company is filtered out if it's in there. */
  companies: FirmNavnId[];
  value: FirmNavnId[];
  onChange: (selected: FirmNavnId[]) => void;
  className?: string;
  /** Smaller lines in the open list, one store per line (SS10, SS12). */
  compact?: boolean;
}

const COMPACT_LISTBOX_SX = {
  fontSize: '0.82rem',
  py: 0.5,
  '& .MuiAutocomplete-option': { minHeight: 28, py: 0, pl: 0.5, whiteSpace: 'nowrap' },
  '& .MuiCheckbox-root': { p: 0.5 },
} as const;
// A long store name widens the list rather than wrapping onto two lines.
const COMPACT_PAPER_SX = { width: 'max-content', minWidth: '100%' } as const;

/** "963 FIRMA A, 964 FIRMA B" for the report title; "5 butikker" when the list gets long. */
export const storeNames = (selected: FirmNavnId[]) =>
  selected.length <= 3 ? selected.map((f) => f.firmName).join(', ') : selected.length + ' butikker';

/** The id the report API takes: one id, or a comma-separated list that is summed together. */
export const storeIds = (selected: FirmNavnId[]) => selected.map((f) => f.firmID).join(',');

// Store list ("Butikk liste"): tick the main company alone, or any subsidiaries (or "Alle
// datterselskaper", then untick single ones) and get one report for all of them together.
// Clicking a ticked line unticks it. The ticked companies are listed first.
const StorePicker = ({ mainCompany, companies, value, onChange, className, compact }: Props) => {
  const subsidiaries = companies.filter((f) => f.firmID !== mainCompany.firmID).sort((a, b) => a.firmID - b.firmID);
  const isSelected = (f: FirmNavnId) => value.some((v) => v.firmID === f.firmID);
  const allSubsidiariesSelected = subsidiaries.length > 0 && subsidiaries.every(isSelected);
  const someSubsidiariesSelected = subsidiaries.some(isSelected);

  const tickedFirst = (list: FirmNavnId[]) => [...list.filter(isSelected), ...list.filter((f) => !isSelected(f))];
  const options = [mainCompany, ...(subsidiaries.length > 0 ? [ALL_SUBSIDIARIES] : []), ...tickedFirst(subsidiaries)];

  // The main company is always alone: ticking it clears the subsidiaries, and ticking a
  // subsidiary (or "Alle datterselskaper") clears the main company.
  const selectedSubsidiaries = value.filter((v) => v.firmID !== mainCompany.firmID);
  const toggle = (f: FirmNavnId) => {
    if (f.firmID === mainCompany.firmID) {
      onChange(isSelected(f) ? [] : [mainCompany]);
    } else if (f.firmID === ALL_SUBSIDIARIES.firmID) {
      onChange(allSubsidiariesSelected ? [] : subsidiaries);
    } else {
      onChange(isSelected(f) ? selectedSubsidiaries.filter((v) => v.firmID !== f.firmID) : [...selectedSubsidiaries, f]);
    }
  };

  return (
    <Autocomplete multiple disableCloseOnSelect size="small" className={className}
      // One line only: the summary text shrinks with an ellipsis instead of wrapping the input below it.
      sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
      slotProps={compact ? { listbox: { sx: COMPACT_LISTBOX_SX }, paper: { sx: COMPACT_PAPER_SX } } : undefined}
      options={options} value={value}
      // Selection is handled per click in toggle(); this only catches clearing with the X button.
      onChange={(_e, items, reason) => { if (reason === 'clear') onChange([]); else if (reason === 'removeOption') onChange(items.filter((i) => i.firmID !== ALL_SUBSIDIARIES.firmID)); }}
      isOptionEqualToValue={(o, v) => o.firmID === v.firmID}
      getOptionLabel={(o) => (o.firmID === ALL_SUBSIDIARIES.firmID ? o.firmName : o.firmID + ' ' + o.firmName)}
      renderTags={(selected) => <span style={{ paddingLeft: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '0 1 auto' }}>
        {selected.length === 1 ? selected[0].firmID + ' ' + selected[0].firmName : selected.length + ' butikker valgt'}</span>}
      renderOption={(props, option) => {
        const { key, onClick: _onClick, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        const isAll = option.firmID === ALL_SUBSIDIARIES.firmID;
        const checked = isAll ? allSubsidiariesSelected : isSelected(option);
        return (
          <li key={key} {...rest} onClick={() => toggle(option)} style={{ fontWeight: isAll || option.firmID === mainCompany.firmID ? 600 : 400 }}>
            <Checkbox size="small" style={{ marginRight: 8 }} checked={checked}
              indeterminate={isAll && !allSubsidiariesSelected && someSubsidiariesSelected}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />}
              indeterminateIcon={<IndeterminateCheckBoxIcon fontSize="small" />} />
            {option.firmID === mainCompany.firmID ? 'Hovedfirma: ' : ''}{isAll ? option.firmName : option.firmID + ' ' + option.firmName}
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} variant="outlined"
        placeholder={value.length === 0 ? 'Velg butikk' : undefined} />}
    />
  );
};

export default StorePicker;
